import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiZap, FiCalendar } = FiIcons;

const HowItWorksSection = () => {
  const steps = [
    {
      number: '01',
      icon: FiTarget,
      title: 'Define Your "Perfect Client"',
      description: 'In a 15-minute call, we understand your target audience, your qualifying criteria, and your goals. We map out your ideal client profile and budget requirements.',
      timeCommitment: 'Your time: just 15 minutes'
    },
    {
      number: '02',
      icon: FiZap,
      title: 'We Build Your AI',
      description: 'Our expert partners at Noem AI build and train a custom AI assistant on your specific business. We handle all the technical work—you don\'t lift a finger.',
      timeCommitment: 'Zero effort required from you'
    },
    {
      number: '03',
      icon: FiCalendar,
      title: 'Launch & Scale',
      description: 'We deploy the AI to your website. It starts qualifying leads and booking high-value appointments from day one. Your calendar fills with pre-qualified, high-intent prospects.',
      timeCommitment: 'Live in 7 days or less'
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            Your Custom AI Assistant, <span className="text-green-600">Live in 7 Days</span>
          </h2>
          <p className="text-2xl text-gray-600">
            Simple 3-Step Process
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="relative bg-gradient-to-br from-gray-50 to-blue-50 p-8 rounded-2xl border-2 border-blue-100 hover:border-blue-300 hover:shadow-xl transition-all duration-300"
            >
              {/* Step Number */}
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                {step.number}
              </div>
              
              {/* Icon */}
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto mt-8">
                <SafeIcon icon={step.icon} className="text-3xl text-blue-600" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                {step.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed mb-6 text-center">
                {step.description}
              </p>
              
              <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 py-3 px-4 rounded-lg text-center">
                <p className="text-sm text-green-800 font-bold">{step.timeCommitment}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Timeline Visual */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="max-w-4xl mx-auto mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-2xl shadow-2xl">
            <h3 className="text-3xl font-bold mb-4">Ready to Launch in Just 7 Days</h3>
            <p className="text-xl text-blue-100 mb-6">From strategy call to live AI assistant on your website</p>
            <div className="flex items-center justify-center gap-4">
              <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
                <p className="text-sm text-blue-100 mb-1">Day 1</p>
                <p className="font-bold">Strategy Call</p>
              </div>
              <div className="text-3xl">→</div>
              <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
                <p className="text-sm text-blue-100 mb-1">Days 2-6</p>
                <p className="font-bold">We Build</p>
              </div>
              <div className="text-3xl">→</div>
              <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
                <p className="text-sm text-blue-100 mb-1">Day 7</p>
                <p className="font-bold">You Launch</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;